import { useCallback, useMemo, useState } from 'react';
import { useMeasure } from 'context/measure';
import { getFocusedAgeLevel } from 'helpers/dimension';
import { useGetObservationCycle } from 'actions/envScales/observationCycles';
import CycleDetailsForm from 'components/Measure/View/Cycles/CycleDetailsForm';
import CycleScoresForm from 'components/Measure/View/Cycles/CycleScoresForm';
import CycleNotesForm from 'components/Measure/View/Cycles/CycleNotesForm';
import { useGetObservationCycleScores } from 'actions/envScales/observationCycleScores';
import BackToTop from 'components/Measure/BackToTop';
import FooterNav from 'components/Measure/View/FooterNav';
import PageLoader from 'components/PageLoader';
import { useRouteParams } from 'hooks/useRouteParams';
import {
  ObservationSettings,
  useObservationSettings
} from 'actions/envScales/observationSettings';
import { ObservationCycle } from 'types/api/envScales/ObservationCycle';
import { ObservationCycleScore } from 'types/api/envScales/ObservationCycleScore';

type ParamsType = {
  id: string;
  cycleId: string;
};

interface CycleViewProps {
  settings: ObservationSettings;
  cycle: ObservationCycle;
  cycleScores: ObservationCycleScore[];
}

function CycleView({ settings, cycle, cycleScores }: CycleViewProps) {
  const { assessment, ageLevel } = useMeasure();

  const [notesByDimensionId, setNotesByDimensionId] = useState(
    getInitialNotesByDimensionId
  );

  // The details form keeps the full age level: it renders meta data
  // categories, which dimensions of focus do not narrow.
  const focusedAgeLevel = useMemo(
    () => getFocusedAgeLevel(ageLevel, assessment),
    [ageLevel, assessment]
  );

  function getInitialNotesByDimensionId() {
    const initialNotes: Record<number, string> = {};

    cycleScores.forEach(score => {
      initialNotes[score.dimension_id] = score.dimension_notes ?? '';
    });

    return initialNotes;
  }

  const handleDimensionNotesChange = useCallback(
    (dimensionId: number, notes: string) => {
      setNotesByDimensionId(prevNotes => ({
        ...prevNotes,
        [dimensionId]: notes
      }));
    },
    []
  );

  return (
    <div className="isolate">
      {cycle && (
        <CycleDetailsForm
          ageLevel={ageLevel}
          assessment={assessment}
          cycle={cycle}
        />
      )}

      {settings.notes_enabled && (
        <CycleNotesForm
          ageLevel={focusedAgeLevel}
          cycleId={cycle.id}
          cycleNumber={cycle.cycle_number}
          notesByDimensionId={notesByDimensionId}
          onNotesByDimensionIdChange={handleDimensionNotesChange}
        />
      )}

      {cycleScores && (
        <CycleScoresForm
          cycle={cycle}
          cycleScores={cycleScores}
          ageLevel={focusedAgeLevel}
          isInformal={assessment.informal}
          isNotesEnabled={settings.notes_enabled}
          notesByDimensionId={notesByDimensionId}
        />
      )}

      <BackToTop />
      <FooterNav />
    </div>
  );
}

function CycleViewController() {
  const { assessment } = useMeasure();
  const { cycleId } = useRouteParams<ParamsType>();
  const observationId = assessment.observation!.id;
  const parsedCycleId = parseInt(cycleId, 10);

  const { data: cycle, isFetching: isCycleLoading } = useGetObservationCycle(
    observationId,
    parsedCycleId
  );

  const { data: cycleScores, isFetching: isCycleScoresLoading } =
    useGetObservationCycleScores(parsedCycleId);

  const { data: settings, isFetching: isSettingsLoading } =
    useObservationSettings({ assessmentId: assessment.id });

  if (isCycleLoading || isCycleScoresLoading || isSettingsLoading) {
    return <PageLoader />;
  }

  return (
    <CycleView
      key={cycleId}
      settings={settings!}
      cycle={cycle!}
      cycleScores={cycleScores!.observation_cycle_scores}
    />
  );
}

export default CycleViewController;
